import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useT } from '@/context/LocaleContext';
import { Music, Home, Library, GraduationCap, MessagesSquare, Sparkles, LogOut, User, Shield, ListMusic, Crown, Trophy, Gift, Star, Mic2, TrendingUp, Mail, Info } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import MaestroPitchBanner, { reopenMaestroPitchBanner } from '@/components/MaestroPitchBanner';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  const nav = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: Home, tid: 'nav-dashboard' },
    { to: '/library', label: t('nav.library'), icon: Library, tid: 'nav-library' },
    { to: '/top-songs', label: 'Top 20', icon: TrendingUp, tid: 'nav-top-songs' },
    { to: '/courses', label: t('nav.courses'), icon: GraduationCap, tid: 'nav-courses' },
    { to: '/playlists', label: t('nav.playlists'), icon: ListMusic, tid: 'nav-playlists' },
    { to: '/challenge', label: t('nav.challenge'), icon: Trophy, tid: 'nav-challenge' },
    { to: '/hall-of-fame', label: t('nav.hall_of_fame'), icon: Star, tid: 'nav-hof' },
    { to: '/tutor', label: t('nav.tutor'), icon: Sparkles, tid: 'nav-tutor' },
    { to: '/community', label: t('nav.community'), icon: MessagesSquare, tid: 'nav-community' },
    { to: '/studio', label: 'Studio', icon: Mic2, tid: 'nav-studio' },
    { to: '/requests', label: 'Demandes', icon: Mail, tid: 'nav-requests' },
    { to: '/referral', label: 'Parrainage', icon: Gift, tid: 'nav-referral' },
  ];

  return (
    <div className="min-h-screen flex text-stone-100">
      <aside className="hidden md:flex fixed top-0 left-0 h-screen w-64 flex-col border-r border-stone-800 bg-stone-950/50 backdrop-blur-xl z-30">
        <Link to="/dashboard" className="px-6 py-6 flex items-center gap-2" data-testid="brand-link">
          <div className="w-9 h-9 rounded-xl bg-orange-700 flex items-center justify-center">
            <Music size={18} />
          </div>
          <div>
            <div className="font-display text-2xl leading-none">Maestro</div>
            <div className="overline text-stone-400">{t('brand.tagline')}</div>
          </div>
        </Link>
        <nav className="mt-4 flex-1 px-3 space-y-1 overflow-y-auto no-scroll-bar">
          {nav.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-orange-700/20 text-orange-300 border border-orange-800/40' : 'text-stone-300 hover:bg-stone-800/60'
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                  isActive ? 'bg-orange-700/20 text-orange-300 border border-orange-800/40' : 'text-stone-300 hover:bg-stone-800/60'
                }`
              }
            >
              <Shield size={18} />
              <span>{t('nav.admin')}</span>
            </NavLink>
          )}
          <NavLink
            to="/pro"
            data-testid="nav-pro"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors border ${
                isActive
                  ? 'bg-orange-700/20 text-orange-300 border-orange-800/40'
                  : user?.plan?.is_pro
                    ? 'text-orange-300 border-orange-800/40 hover:bg-orange-950/30'
                    : 'text-stone-300 border-transparent hover:bg-stone-800/60'
              }`
            }
          >
            <Crown size={18} />
            <span>{user?.plan?.is_pro ? t('nav.member_pro') : t('nav.pro')}</span>
          </NavLink>
        </nav>
        <div className="p-4 border-t border-stone-800 space-y-2">
          <LanguageSelector />
          <button
            data-testid="reopen-pitch-banner"
            onClick={reopenMaestroPitchBanner}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-stone-400 hover:bg-stone-800/60 text-sm"
            title="Rouvrir le message de bienvenue"
          >
            <Info size={16} /> À propos de Maestro
          </button>
          <button
            data-testid="profile-link"
            onClick={() => navigate('/profile')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-stone-800/60"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center"><User size={16} /></div>
            )}
            <div className="text-left">
              <div className="text-sm">{user?.name || 'Musicien'}</div>
              <div className="text-xs text-stone-500 truncate max-w-[140px]">{user?.email}</div>
            </div>
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-stone-400 hover:bg-stone-800/60 text-sm"
          >
            <LogOut size={16} /> {t('nav.logout')}
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64 min-h-screen relative z-10">
        <div className="px-6 md:px-10 pt-6"><MaestroPitchBanner /></div>
        {children}
      </main>
    </div>
  );
}
